// =====================================================================================
// INVRT Radar - web console - operator organisations read (Story PS1)
//
// One cache entry shared by the console header's organisation selector and the access
// section, so the selector and the section can never disagree about which organisations
// exist while the wire is read exactly once. The reader walks the reviewed E04A pager to
// exhaustion with repeated-cursor refusal, using the exact fail-closed parsers the access
// section has always used.
// =====================================================================================

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { ApiError } from "@/lib/api";
import type { AdminOrganisation } from "@/lib/types.entitlements";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

function invalidContract(message: string): ApiError {
  return new ApiError({ token: "internal_error", message, httpStatus: 502, correlationId: null });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const received = Object.keys(value).sort();
  const expected = [...keys].sort();
  return received.length === expected.length && received.every((key, index) => key === expected[index]);
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function isDateTime(value: unknown): value is string {
  if (typeof value !== "string" || !DATE_TIME_PATTERN.test(value)) return false;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:\d{2})$/);
  if (!match) return false;
  const year = match[1]!;
  const month = match[2]!;
  const day = match[3]!;
  const hour = match[4]!;
  const minute = match[5]!;
  const second = match[6]!;
  const offset = match[7]!;
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (m < 1 || m > 12 || hour > "23" || minute > "59" || second > "59") return false;
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (d < 1 || d > daysInMonth) return false;
  if (offset !== "Z") {
    const offsetHours = Number(offset.slice(1, 3));
    const offsetMinutes = Number(offset.slice(4, 6));
    if (offsetHours > 23 || offsetMinutes > 59) return false;
  }
  return true;
}

function isCursor(value: unknown): value is string | null {
  return value === null || (typeof value === "string" && value.length >= 1 && value.length <= 1024);
}

export function isOrganisation(value: unknown): value is AdminOrganisation {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["id", "name", "status", "created_at"]) &&
    isUuid(value.id) &&
    typeof value.name === "string" &&
    ["active", "suspended", "closed"].includes(String(value.status)) &&
    isDateTime(value.created_at)
  );
}

function isOrganisationPage(value: unknown): value is { organisations: AdminOrganisation[]; next_cursor: string | null } {
  return (
    isRecord(value) &&
    Object.keys(value).length === 2 &&
    "organisations" in value &&
    "next_cursor" in value &&
    Array.isArray(value.organisations) &&
    value.organisations.every(isOrganisation) &&
    isCursor(value.next_cursor)
  );
}

async function readAllOrganisations(
  api: ReturnType<typeof useApiClient>,
): Promise<AdminOrganisation[]> {
  const rows: AdminOrganisation[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  for (;;) {
    const response = await api.get<unknown>("/admin/organisations", {
      requiresOrg: false,
      includeOrg: false,
      query: { limit: 100, cursor },
    });
    if (!isOrganisationPage(response)) {
      throw invalidContract("The organisations response did not match the reviewed E04A contract.");
    }
    rows.push(...response.organisations);
    const next = response.next_cursor;
    if (next === null) return rows;
    if (seen.has(next)) {
      throw invalidContract("The organisations response repeated a pagination cursor.");
    }
    seen.add(next);
    cursor = next;
  }
}

export function useOperatorOrganisations(options: { enabled?: boolean } = {}) {
  const api = useApiClient();
  return useQuery({
    queryKey: ["operator", "access", "organisations"],
    queryFn: () => readAllOrganisations(api),
    staleTime: 30_000,
    // A non-super-admin must never send a privileged request from this page: the console
    // denies before anything leaves the browser.
    enabled: options.enabled !== false,
  });
}
